import React, { useEffect, useState } from 'react';
import { ResponsiveCirclePacking } from '@nivo/circle-packing';
import { Info, X } from 'lucide-react';
import { PALETA_CATEGORICA, TEXTO_PRIMARIO, TEXTO_SECUNDARIO } from './paletaResultados';

const DOIS_MINUTOS_MS = 2 * 60 * 1000;

// Frequência de palavra de pergunta aberta — bolhas em vez de nuvem de
// texto tradicional (nenhuma lib de nuvem de palavras atual é compatível
// com React 18 neste projeto). Cada bolha é uma palavra, tamanho
// proporcional à frequência.
//
// A prop de identidade do Nivo se chama `id` (não `identity` — engano
// original que derrubou cor, rótulo da bolha e o clique fixado, todos ao
// mesmo tempo: sem ela reconhecida, o Nivo caía no padrão `id: "id"`,
// procurando um campo que os dados aqui nunca tiveram, então toda bolha
// virava identidade `undefined`). colorBy="id" + array de cores é o
// mecanismo próprio do Nivo pra "colorir por identidade" — sem isso, o
// padrão é colorir por profundidade da hierarquia (`colorBy: 'depth'`), e
// como toda palavra é filha direta da raiz (mesma profundidade), todas
// cairiam na mesma cor de qualquer forma.
const GraficoBolhas = ({ palavras }) => {
  const [fixada, setFixada] = useState(null); // { id, value } ou null

  // Clicar numa bolha fixa a palavra na tela por 2 minutos; clicar em outra
  // antes disso cancela o timer anterior e recomeça a partir da nova palavra
  // (efeito reroda porque `fixada` muda — o cleanup cancela o setTimeout
  // velho automaticamente antes de agendar o novo).
  useEffect(() => {
    if (!fixada) return undefined;
    const temporizador = setTimeout(() => setFixada(null), DOIS_MINUTOS_MS);
    return () => clearTimeout(temporizador);
  }, [fixada]);

  if (!palavras || palavras.length === 0) {
    return <p className="text-sm text-gray-400 italic py-6 text-center">Nenhuma palavra registrada ainda.</p>;
  }

  const dados = {
    name: 'raiz',
    children: palavras.map((p) => ({ name: p.palavra, value: p.total }))
  };

  return (
    <div>
      <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 mb-2.5">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: TEXTO_SECUNDARIO }} />
        <p>Passe o mouse numa bolha pra ver rápido, ou clique pra fixar a palavra na tela por 2 minutos.</p>
      </div>

      {fixada && (
        <div className="flex items-center justify-between gap-2 bg-primary-50 border border-primary-100 rounded-lg px-3 py-2 mb-2.5 animate-fade-in-up">
          <p className="text-sm min-w-0 truncate">
            <span className="font-semibold text-gray-900">{fixada.id}</span>
            <span className="text-gray-500"> — {fixada.value.toLocaleString('pt-BR')} menção(ões)</span>
          </p>
          <button
            type="button"
            onClick={() => setFixada(null)}
            aria-label="Fechar"
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div style={{ height: 320 }}>
        <ResponsiveCirclePacking
          data={dados}
          margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
          id="name"
          value="value"
          leavesOnly
          padding={6}
          colorBy="id"
          colors={PALETA_CATEGORICA}
          borderWidth={0}
          enableLabels
          label={(d) => d.id}
          labelsFilter={(d) => d.node.radius > 18}
          labelTextColor={TEXTO_PRIMARIO}
          labelsSkipRadius={18}
          animate
          motionConfig="gentle"
          role="img"
          ariaLabel="Gráfico de bolhas com as palavras mais frequentes"
          onClick={(node) => setFixada({ id: node.id, value: node.value })}
          tooltip={({ id, value, color }) => (
            <div className="bg-white shadow-lg rounded-lg pl-2.5 pr-3.5 py-2 border border-gray-100 flex items-stretch gap-2.5">
              <span className="w-[3px] rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
              <div>
                <p className="text-base font-semibold text-gray-900 leading-tight">{id}</p>
                <p className="text-xs text-gray-500">{value.toLocaleString('pt-BR')} menção(ões)</p>
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default GraficoBolhas;
